import React, { useCallback, useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import MenuButton from "../MenuButton/MenuButton";
import { NoteState } from "../../context/notes/NoteState";
import UserServices from "../../UserServices/UserServices";
import DashboardBranchFilter, {
  useDashboardBranch,
} from "../Includes/DashboardBranchFilter";
import "../AdminDashboard/AdminDashboard.css";

const BRAND = "#e8002b";
const TEAL = "#0f766e";
const AMBER = "#d97706";
const BLUE = "#2563eb";
const PIE = [TEAL, BLUE, AMBER, BRAND];

const money = (v) =>
  `Rs ${Number(v || 0).toLocaleString("en-PK", { maximumFractionDigits: 0 })}`;
const num = (v) => Number(v || 0).toLocaleString("en-PK");

const KIND = {
  guest: "Guest",
  client: "Client",
  staff: "Staff",
  management: "Management",
};

const REPORTS = [
  { to: "/cafe-sales-report", label: "Cafe Sales Report" },
  { to: "/detailed-cafe-report", label: "Detailed Cafe Report" },
  { to: "/new-cafe-report", label: "New Cafe Report" },
  { to: "/cafe-sales-expense-report", label: "Sales & Expense Report" },
  { to: "/cafe-sales", label: "Cafe Sales" },
  { to: "/management-pendings", label: "Management Pendings" },
];

const Metric = ({ label, value }) => (
  <div className="ad-metric">
    <span className="ad-metric-label">{label}</span>
    <strong className="ad-metric-value">{value}</strong>
  </div>
);

const Kpi = ({ label, value, sub, tone = "slate", icon }) => (
  <div className={`ad-kpi ad-tone-${tone}`}>
    <div className="ad-kpi-body">
      <p className="ad-kpi-label">{label}</p>
      <h4 className="ad-kpi-value">{value}</h4>
      {sub ? <p className="ad-kpi-sub">{sub}</p> : null}
    </div>
    <div className="ad-kpi-icon">
      <i className={`bi ${icon}`}></i>
    </div>
  </div>
);

export default function CafeOverviewDashboard() {
  const { state } = useContext(NoteState);
  const { role, branch_id } = state;
  const { branch, setBranch } = useDashboardBranch(role, branch_id);
  const [date, setDate] = useState(moment().format("YYYY-MM-DD"));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await UserServices.cafeDashboardSummary(branch, { date, timeout: 25000 });
      setData(res?.data?.data || null);
    } catch (err) {
      setError(err?.response?.data?.message || "Cafe dashboard could not be loaded.");
    } finally {
      setLoading(false);
    }
  }, [branch, date]);

  useEffect(() => {
    load();
  }, [load]);

  const today = data?.today || {};
  const month = data?.month || {};
  const buyers = today.buyers || {};
  const monthBuyers = month.buyers || {};
  const branchLabel = data?.branch_label || (branch === "1" ? "G-13" : branch === "15" ? "F-11" : "All Branches");
  const buyerChart = ["guest", "client", "staff", "management"].map((key) => ({
    name: KIND[key],
    value: Number(monthBuyers[key]?.net || 0),
  }));
  const buyerCompare = ["guest", "client", "staff", "management"].map((key) => ({
    name: KIND[key],
    today: Number(buyers[key]?.net || 0),
    month: Number(monthBuyers[key]?.net || 0),
  }));
  const trend = (data?.trend || []).map((row) => ({
    ...row,
    profit: Number(row.sales || 0) - Number(row.expenses || 0),
  }));
  const groups = [
    { name: "Meals", today: Number(today.groups?.meals || 0), month: Number(month.groups?.meals || 0) },
    { name: "Drinks", today: Number(today.groups?.drinks || 0), month: Number(month.groups?.drinks || 0) },
    { name: "Sides", today: Number(today.groups?.sides || 0), month: Number(month.groups?.sides || 0) },
  ];
  const buyerPie = buyerChart.filter((row) => row.value > 0);
  const year = data?.year || {};
  const paymentPie = (data?.payments || [])
    .filter((row) => Number(row.received) > 0)
    .map((row) => ({ name: row.name, value: Number(row.received) }));

  return (
    <div className="wrapper">
      <main className="page-content customer-contnent">
        <div className="site-main-design ad-shell">
          <MenuButton />
          <div className="container-fluid px-3 px-md-4 pb-4">
            <div className="ad-topbar">
              <div>
                <p className="ad-eyebrow">Cafe</p>
                <h2 className="ad-title">
                  <i className="bi bi-cup-hot me-2"></i>
                  Cafe Dashboard
                </h2>
                <p className="ad-subtitle">
                  {moment(data?.date || date).format("dddd, DD MMM YYYY")}
                  {" · "}
                  {moment(data?.month_start || date).format("MMM YYYY")}
                </p>
              </div>
              <div className="ad-toolbar">
                <DashboardBranchFilter
                  role={role}
                  loginBranchId={branch_id}
                  value={branch}
                  onChange={setBranch}
                />
                <input
                  type="date"
                  className="form-control"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  aria-label="Date"
                />
                <button type="button" className="btn btn-outline-danger" onClick={load} disabled={loading}>
                  {loading ? "Loading…" : "Refresh"}
                </button>
              </div>
            </div>

            <div className="ad-panel mb-3">
              <div className="ad-panel-head">
                <h5>Cafe reports</h5>
              </div>
              <div className="d-flex flex-wrap gap-2 px-3 pb-3">
                {REPORTS.map((report) => (
                  <Link key={report.to} to={report.to} className="btn btn-outline-danger btn-sm">
                    {report.label}
                  </Link>
                ))}
              </div>
            </div>

            {error ? <div className="alert alert-warning py-2">{error}</div> : null}

            {loading && !data ? (
              <div className="text-center py-5">
                <div className="spinner-border text-danger" role="status" />
                <div className="text-muted mt-2 small">Loading cafe dashboard…</div>
              </div>
            ) : (
              <>
                <h3 className="ad-section-title">Today · {branchLabel}</h3>
                <div className="ad-kpi-grid">
                  <Kpi label="Today sales" value={money(today.net)} sub={`Gross ${money(today.gross)} · Tax ${money(today.tax)}`} icon="bi-cash-stack" tone="red" />
                  <Kpi label="Today orders" value={num(today.orders)} sub={`${num(today.qty)} items · Avg ${money(today.avg_ticket)}`} icon="bi-receipt" tone="slate" />
                  <Kpi label="Today expenses" value={money(today.expenses)} sub="Cafe expenses" icon="bi-wallet2" tone="amber" />
                  <Kpi label="Today profit" value={money(today.profit)} sub="Sales minus cafe expenses" icon="bi-graph-up-arrow" tone="slate" />
                  <Kpi label="Month sales" value={money(month.net)} sub={`${num(month.orders)} orders · Discount ${money(month.discount)}`} icon="bi-calendar3" tone="red" />
                  <Kpi label="Month expenses" value={money(month.expenses)} sub={`Profit ${money(month.profit)}`} icon="bi-piggy-bank" tone="amber" />
                  <Kpi label="Guest sales" value={money(buyers.guest?.net)} sub={`${num(buyers.guest?.orders)} today · Month ${money(monthBuyers.guest?.net)}`} icon="bi-person" tone="slate" />
                  <Kpi label="Client sales" value={money(buyers.client?.net)} sub={`${num(buyers.client?.orders)} today · Month ${money(monthBuyers.client?.net)}`} icon="bi-people" tone="red" />
                  <Kpi label="Staff sales" value={money(buyers.staff?.net)} sub={`${num(buyers.staff?.orders)} today · Month ${money(monthBuyers.staff?.net)}`} icon="bi-person-badge" tone="slate" />
                  <Kpi label="Management sales" value={money(buyers.management?.net)} sub={`${num(buyers.management?.orders)} today · Month ${money(monthBuyers.management?.net)}`} icon="bi-briefcase" tone="amber" />
                  <Kpi label="Meals / drinks / sides" value={`${num(today.groups?.meals)} / ${num(today.groups?.drinks)} / ${num(today.groups?.sides)}`} sub={`Month ${num(month.groups?.meals)} / ${num(month.groups?.drinks)} / ${num(month.groups?.sides)}`} icon="bi-cup-straw" tone="slate" />
                  <Kpi label="Client deposits" value={money(today.deposits)} sub={`Month ${money(month.deposits)}`} icon="bi-safe" tone="red" />
                  <Kpi label={`${year.label || moment(date).format("YYYY")} sales`} value={money(year.sales)} sub="January through this month" icon="bi-calendar-range" tone="red" />
                  <Kpi label={`${year.label || moment(date).format("YYYY")} expenses`} value={money(year.expenses)} sub="Cafe expenses" icon="bi-wallet2" tone="amber" />
                  <Kpi label={`${year.label || moment(date).format("YYYY")} profit`} value={money(year.profit)} sub="Year sales minus cafe expenses" icon="bi-graph-up" tone="slate" />
                </div>

                {data?.is_all_branches && Array.isArray(data.by_branch) && data.by_branch.length ? (
                  <div className="row g-3 mb-4">
                    {data.by_branch.map((row) => (
                      <div className="col-12 col-md-6" key={row.branch_id}>
                        <div className="ad-panel">
                          <div className="ad-panel-head">
                            <h5>{row.branch_label}</h5>
                            <span className="ad-chip">Branch</span>
                          </div>
                          <div className="ad-metrics">
                            <Metric label="Today sales" value={money(row.today_net)} />
                            <Metric label="Today orders" value={num(row.today_orders)} />
                            <Metric label="Today profit" value={money(row.today_profit)} />
                            <Metric label="Month sales" value={money(row.month_net)} />
                            <Metric label="Month expenses" value={money(row.month_expenses)} />
                            <Metric label="Month profit" value={money(row.month_profit)} />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}

                <h3 className="ad-section-title">{year.label || moment(date).format("YYYY")} · sales and expenses</h3>
                <div className="row g-3 mb-4">
                  <div className="col-12">
                    <div className="ad-panel">
                      <div className="ad-panel-head">
                        <h5>Yearly sales, expenses, and profit</h5>
                        <span className="ad-chip">By month</span>
                      </div>
                      <div className="ad-chart">
                        <ResponsiveContainer width="100%" height={300}>
                          <BarChart data={year.months || []} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                            <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#64748b" }} />
                            <YAxis tick={{ fontSize: 12, fill: "#64748b" }} width={64} />
                            <Tooltip formatter={(v) => money(v)} />
                            <Legend />
                            <Bar dataKey="sales" name="Sales" fill={BRAND} radius={[6, 6, 0, 0]} />
                            <Bar dataKey="expenses" name="Expenses" fill={AMBER} radius={[6, 6, 0, 0]} />
                            <Bar dataKey="profit" name="Profit" fill={TEAL} radius={[6, 6, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>
                </div>

                <h3 className="ad-section-title">This month</h3>
                <div className="row g-3 mb-4">
                  <div className="col-12 col-xl-7">
                    <div className="ad-panel">
                      <div className="ad-panel-head">
                        <h5>Sales vs expenses</h5>
                        <span className="ad-chip">Daily</span>
                      </div>
                      <div className="ad-chart">
                        <ResponsiveContainer width="100%" height={280}>
                          <LineChart data={trend} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                            <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748b" }} interval="preserveStartEnd" />
                            <YAxis tick={{ fontSize: 12, fill: "#64748b" }} width={64} />
                            <Tooltip formatter={(v) => money(v)} />
                            <Legend />
                            <Line type="monotone" dataKey="sales" name="Sales" stroke={BRAND} strokeWidth={2.5} dot={false} />
                            <Line type="monotone" dataKey="expenses" name="Expenses" stroke={AMBER} strokeWidth={2} dot={false} />
                            <Line type="monotone" dataKey="profit" name="Profit" stroke={TEAL} strokeWidth={2} dot={false} />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>
                  <div className="col-12 col-xl-5">
                    <div className="ad-panel h-100">
                      <div className="ad-panel-head">
                        <h5>Sales by buyer</h5>
                        <span className="ad-chip">Month</span>
                      </div>
                      <div className="ad-chart">
                        <ResponsiveContainer width="100%" height={280}>
                          {buyerPie.length ? (
                            <PieChart>
                              <Pie data={buyerPie} dataKey="value" nameKey="name" innerRadius={58} outerRadius={90} paddingAngle={2}>
                                {buyerPie.map((entry, index) => (
                                  <Cell key={entry.name} fill={PIE[index % PIE.length]} />
                                ))}
                              </Pie>
                              <Tooltip formatter={(v) => money(v)} />
                              <Legend />
                            </PieChart>
                          ) : (
                            <div className="text-muted small text-center py-5">No buyer sales this month.</div>
                          )}
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="row g-3 mb-4">
                  <div className="col-12 col-xl-7">
                    <div className="ad-panel h-100">
                      <div className="ad-panel-head">
                        <h5>Guest, client, staff, management</h5>
                        <span className="ad-chip">Sales</span>
                      </div>
                      <div className="ad-chart">
                        <ResponsiveContainer width="100%" height={280}>
                          <BarChart data={buyerCompare} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                            <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#64748b" }} />
                            <YAxis tick={{ fontSize: 12, fill: "#64748b" }} width={64} />
                            <Tooltip formatter={(v) => money(v)} />
                            <Legend />
                            <Bar dataKey="today" name="Today" fill={BRAND} radius={[6, 6, 0, 0]} />
                            <Bar dataKey="month" name="This month" fill={TEAL} radius={[6, 6, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>
                  <div className="col-12 col-xl-5">
                    <div className="ad-panel h-100">
                      <div className="ad-panel-head">
                        <h5>Meals, drinks, sides</h5>
                        <span className="ad-chip">Items</span>
                      </div>
                      <div className="ad-chart">
                        <ResponsiveContainer width="100%" height={280}>
                          <BarChart data={groups} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                            <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#64748b" }} />
                            <YAxis tick={{ fontSize: 12, fill: "#64748b" }} width={40} allowDecimals={false} />
                            <Tooltip formatter={(v) => num(v)} />
                            <Legend />
                            <Bar dataKey="today" name="Today" fill={AMBER} radius={[6, 6, 0, 0]} />
                            <Bar dataKey="month" name="This month" fill={BLUE} radius={[6, 6, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>
                </div>

                {data?.is_all_branches && (data.by_branch || []).length ? (
                  <div className="row g-3 mb-4">
                    <div className="col-12">
                      <div className="ad-panel">
                        <div className="ad-panel-head">
                          <h5>Branch sales</h5>
                          <span className="ad-chip">This month</span>
                        </div>
                        <div className="ad-chart">
                          <ResponsiveContainer width="100%" height={260}>
                            <BarChart data={data.by_branch} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                              <XAxis dataKey="branch_label" tick={{ fontSize: 12, fill: "#64748b" }} />
                              <YAxis tick={{ fontSize: 12, fill: "#64748b" }} width={64} />
                              <Tooltip formatter={(v) => money(v)} />
                              <Legend />
                              <Bar dataKey="today_net" name="Today sales" fill={BRAND} radius={[6, 6, 0, 0]} />
                              <Bar dataKey="month_net" name="Month sales" fill={TEAL} radius={[6, 6, 0, 0]} />
                              <Bar dataKey="month_profit" name="Month profit" fill={BLUE} radius={[6, 6, 0, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : null}

                <div className="row g-3 mb-4">
                  <div className="col-12 col-xl-5">
                    <div className="ad-panel h-100">
                      <div className="ad-panel-head">
                        <h5>Top sold items</h5>
                        <span className="ad-chip">This month</span>
                      </div>
                      <div className="ad-chart">
                        <ResponsiveContainer width="100%" height={280}>
                          <BarChart data={data?.top_items || []} layout="vertical" margin={{ top: 8, right: 12, left: 8, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                            <XAxis xAxisId="qty" type="number" tick={{ fontSize: 12, fill: "#64748b" }} />
                            <XAxis xAxisId="net" type="number" orientation="top" tick={{ fontSize: 11, fill: "#64748b" }} />
                            <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11, fill: "#64748b" }} />
                            <Tooltip formatter={(v, key) => (key === "net" ? money(v) : num(v))} />
                            <Legend />
                            <Bar xAxisId="qty" dataKey="qty" name="Qty" fill={BRAND} radius={[0, 6, 6, 0]} />
                            <Bar xAxisId="net" dataKey="net" name="Amount" fill={TEAL} radius={[0, 6, 6, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>
                  <div className="col-12 col-xl-7">
                    <div className="ad-panel h-100">
                      <div className="ad-panel-head">
                        <h5>Orders today</h5>
                        <span className="ad-chip">{num(today.orders)}</span>
                      </div>
                      <div className="ad-table-wrap">
                        <table className="ad-table">
                          <thead>
                            <tr>
                              <th>Time</th>
                              <th>Buyer</th>
                              <th>Type</th>
                              <th>Items</th>
                              <th>Net</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(data?.orders || []).length ? (
                              data.orders.map((row) => (
                                <tr key={row.id}>
                                  <td>{row.time || "—"}{data?.is_all_branches ? ` · ${row.branch}` : ""}</td>
                                  <td>{row.buyer}</td>
                                  <td>{KIND[row.kind] || row.kind}</td>
                                  <td>{row.items || "—"}</td>
                                  <td>{money(row.net)}</td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan={5}>No cafe orders for this day.</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="row g-3 mb-4">
                  <div className="col-12">
                    <div className="ad-panel h-100">
                      <div className="ad-panel-head">
                        <h5>Sales by payment method</h5>
                        <span className="ad-chip">This month</span>
                      </div>
                      <div className="row g-3">
                      <div className="col-12 col-lg-5">
                      <div className="ad-chart">
                        <ResponsiveContainer width="100%" height={240}>
                          {paymentPie.length ? (
                            <PieChart>
                              <Pie data={paymentPie} dataKey="value" nameKey="name" innerRadius={52} outerRadius={82} paddingAngle={2}>
                                {paymentPie.map((entry, index) => (
                                  <Cell key={entry.name} fill={PIE[index % PIE.length]} />
                                ))}
                              </Pie>
                              <Tooltip formatter={(v) => money(v)} />
                              <Legend />
                            </PieChart>
                          ) : (
                            <div className="text-muted small text-center py-5">No payment sales this month.</div>
                          )}
                        </ResponsiveContainer>
                      </div>
                      </div>
                      <div className="col-12 col-lg-7">
                      <div className="ad-chart">
                        <ResponsiveContainer width="100%" height={240}>
                          <BarChart data={data?.payments || []} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                            <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#64748b" }} interval={0} />
                            <YAxis tick={{ fontSize: 12, fill: "#64748b" }} width={64} />
                            <Tooltip formatter={(v) => money(v)} />
                            <Legend />
                            <Bar dataKey="today" name="Today" fill={BRAND} radius={[6, 6, 0, 0]} />
                            <Bar dataKey="received" name="This month" fill={TEAL} radius={[6, 6, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="ad-table-wrap">
                        <table className="ad-table">
                          <thead>
                            <tr>
                              <th>Method</th>
                              <th>Today</th>
                              <th>This month</th>
                              <th>Pending</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(data?.payments || []).length ? (
                              data.payments.map((row) => (
                                <tr key={row.name}>
                                  <td>{row.name}</td>
                                  <td>{money(row.today)}</td>
                                  <td>{money(row.received)}</td>
                                  <td>{money(row.pending)}</td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan={4}>No payments in this month.</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                      </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-12 col-lg-6">
                    <div className="ad-panel h-100">
                      <div className="ad-panel-head">
                        <h5>Cafe expenses today</h5>
                      </div>
                      <div className="ad-table-wrap">
                        <table className="ad-table">
                          <thead>
                            <tr>
                              <th>Description</th>
                              {data?.is_all_branches ? <th>Branch</th> : null}
                              <th>Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(data?.expense_rows || []).length ? (
                              data.expense_rows.map((row, index) => (
                                <tr key={`${row.description}-${index}`}>
                                  <td>{row.description}</td>
                                  {data?.is_all_branches ? <td>{row.branch}</td> : null}
                                  <td>{money(row.amount)}</td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan={data?.is_all_branches ? 3 : 2}>No cafe expenses for this day.</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
